import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListDeconsolidationQuery } from "store/services/deconsolidationApi";
import { useAppMeta, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const DeconsolidationListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Deconsolidations" }];

  const deconsolidationTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Deconsolidation ID",
      selector: (row) => row?.deconsolidationId,
      cell: (row) => {
        const displayId = row?.deconsolidationId;
        const path = `/app/crm/deconsolidation/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Shipment",
      selector: (row) => row?.shipmentId?.shipmentId,
      cell: (row) => {
        const formatShipmentId = (shipmentId) =>
          shipmentId.replace(/(.{4})/g, "$1-").slice(0, -1);
        return row.shipmentId ? (
          Array.isArray(row.shipmentId) ? (
            <Tooltip
              title={
                <div>
                  {row.shipmentId.map((shipment, index) => (
                    <Typography key={index} variant="body2">
                      {shipment.IsReturning
                        ? `${formatShipmentId(shipment.shipmentId)} (R)`
                        : formatShipmentId(shipment.shipmentId)} 
                    </Typography>
                  ))}
                </div>
              
              }
              arrow
            >
              <span>
                <Typography component="span" variant="body1">
                  {row.shipmentId.length > 0
                    ? `${formatShipmentId(row.shipmentId[0].shipmentId)}${
                        row.shipmentId[0].IsReturning ? " (R)" : ""
                      }`
                    : "Multiple Shipments"}
                </Typography>

                {row.shipmentId.length > 1 && (
                  <Typography variant="caption" color="textSecondary">
                    +{row.shipmentId.length - 1} more
                  </Typography>
                )}
              </span>
            </Tooltip>
          ) : (
            <Typography component="span" variant="body1">
              {row.shipmentId.IsReturning
                ? `${formatShipmentId(row.shipmentId.shipmentId)} (R)`
                : formatShipmentId(row.shipmentId.shipmentId)}
            </Typography>
          )
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Type",
      selector: (row) => row?.type,
      cell: (row) => row?.type,
    },
     {
      name: "Created By",
      selector: (row) => row.createdBy?.name || "N/A",
      cell: (row) => row.createdBy?.name || "N/A",
    },
 {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/deconsolidation/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Deconsolidation"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/deconsolidation/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Deconsolidation">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchDeconsolidations,
    { data: deconsolidationRes, isLoading, isFetching, error },
  ] = useLazyListDeconsolidationQuery();

  React.useEffect(() => {
    fetchDeconsolidations({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchDeconsolidations, keyword, perPage]);

  useAppMeta(`${activeTab} Deconsolidations`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={deconsolidationTabs} activeTab={activeTab} />
        <TableFilter
          searchLabel="Deconsolidation ID / Shipment ID"
          disabled={isLoading || isFetching}
        ></TableFilter>
        <AppTable
          columns={columns}
          data={deconsolidationRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={deconsolidationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DeconsolidationListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/deconsolidation/create">
      <Button startIcon={<Add />} variant="contained" color="primary">
        Add Deconsolidation
      </Button>
    </Link>
  );
};
